/**
 * The first thing said about a pin, typed where the pin is (`KLQ-20260820-001` phase 4).
 *
 * **It is the pin.** Nothing has been written when this appears — the marker under it is a
 * pending one the client drew, and the POST that creates the pin carries this text as its
 * `body`. That is `PinCreateRequest.body`'s whole reason for existing: *"so that 'drop a pin
 * and type a comment' is one transaction rather than two requests with a window between them
 * in which an unexplained pin is visible to everybody on the project"*. Cancelling therefore
 * leaves nothing behind, anywhere, which is why Cancel is offered rather than a deletion.
 *
 * **Empty is not a pin.** The send control stays disabled until there is something to say. A
 * pin with no comment is a legitimate row server-side (`body` is optional) and this screen
 * still does not make one: on the sheet it would be a number the rail cannot explain.
 *
 * Positioned by `PinLayer`, which owns the projection. This component knows only that it sits
 * where it was put.
 */

import { useEffect, useRef, useState } from 'react';

import { Button, InlineError } from '@/components';
import { useMessages } from '@/lib/i18n';

import { pinErrorKey } from './problems';
import styles from './Viewer.module.css';

export interface PinComposerProps {
  /** True while the one request is in flight. */
  readonly sending: boolean;
  /** The failure of the last attempt, if it failed. The draft survives it. */
  readonly error: unknown;
  readonly onSend: (body: string) => void;
  readonly onCancel: () => void;
}

export function PinComposer({ sending, error, onSend, onCancel }: PinComposerProps) {
  const t = useMessages('drawings');
  const [draft, setDraft] = useState('');
  const field = useRef<HTMLTextAreaElement | null>(null);

  // The press placed the pin; the keyboard should already be in the box. On a tablet this is
  // the difference between one gesture and two, and the pin is worthless until the comment
  // exists — there is nothing else on this panel to do first.
  useEffect(() => {
    field.current?.focus();
  }, []);

  return (
    <div
      className={styles.pinComposer}
      onKeyDown={(event) => {
        // Escape abandons the pin, matching every other dismissable surface in the app. It is
        // caught here rather than on the document, so it cannot swallow an Escape meant for
        // something above this screen.
        if (event.key === 'Escape') {
          event.stopPropagation();
          onCancel();
        }
      }}
    >
      <textarea
        ref={field}
        className={styles.pinField}
        value={draft}
        rows={3}
        placeholder={t('pin.placeholder')}
        aria-label={t('pin.placeholder')}
        onChange={(event) => {
          setDraft(event.currentTarget.value);
        }}
      />
      {error == null ? null : <InlineError variant="inline">{t(pinErrorKey(error))}</InlineError>}
      <span className={styles.pinActions}>
        <Button variant="tertiary" size="sm" onClick={onCancel}>
          {t('pin.cancel')}
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={draft.trim() === '' || sending}
          onClick={() => {
            onSend(draft.trim());
          }}
        >
          {t('pin.send')}
        </Button>
      </span>
    </div>
  );
}
